<template>
  <div v-html="descriptionContent"></div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from 'vue'
import { fetchVehicleDescription } from '@/apis'
import type { RootProps } from '@/types'
import DOMPurify from 'dompurify'

const descriptionContent = ref('')

onMounted(async () => {
  const accountId = getAccountId()
  const vin = getVIN()

  if (accountId === '' || vin === '') {
    console.error('Account ID or VIN is missing. Cannot fetch vehicle description.')
    return
  }

  try {
    const description = await fetchVehicleDescription({ accountId, vin })
    descriptionContent.value = DOMPurify.sanitize(description?.default?.value ?? '', {
      // Only allow safe tags
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'u'],
      // Remove all attributes (including event handlers)
      ALLOWED_ATTR: ['href'], // Only allow specific safe attributes
      // Block javascript: URLs
      ALLOW_UNKNOWN_PROTOCOLS: false,
    })
  } catch (error) {
    console.log('Error fetching settings:', error)
  }
})

const getAccountId = (): string => {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const accountIdFromUrl = urlParams.get('account_id')

  if (accountIdFromUrl && accountIdFromUrl !== '') {
    return accountIdFromUrl
  }

  // Check DOM attributes
  const app = getCurrentInstance()
  const rootProps = app?.proxy?.$root?.$attrs as RootProps | undefined
  const accountIdFromElement = rootProps?.accountId ?? ''

  if (accountIdFromElement && accountIdFromElement !== '') {
    return accountIdFromElement
  }

  return ''
}

const getVIN = (): string => {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const vinFromUrl = urlParams.get('vin')

  if (vinFromUrl && vinFromUrl !== '') {
    return vinFromUrl
  }

  // Check URL path
  const vinFromPath = extractVinFromUrl(window.location.href)

  if (vinFromPath && vinFromPath !== '') {
    return vinFromPath
  }

  // Check DOM attributes
  const app = getCurrentInstance()
  const rootProps = app?.proxy?.$root?.$attrs as RootProps | undefined
  const vinFromElement = rootProps?.vin ?? ''

  if (vinFromElement && vinFromElement !== '') {
    return vinFromElement
  }

  return ''
}

const extractVinFromUrl = (url: string): string | null => {
  try {
    // Create a URL object to parse the URL
    const parsedUrl = new URL(url)

    // Split the pathname by '/'
    const pathSegments = parsedUrl.pathname.split('/').filter((segment) => segment)

    // Check each path segment for a valid VIN
    for (const segment of pathSegments) {
      // VIN validation (17 alphanumeric characters, excluding I, O, Q)
      if (/^[A-HJ-NPR-Z\d]{17}$/.test(segment)) {
        return segment
      }
    }

    return ''
  } catch (error) {
    return ''
  }
}
</script>
